import { Button, Card, Text, Tooltip } from '$components'
import { EditIcon } from '$components'
import { DAY_OF_WEEK } from '../forms/working-hours'
import { Fragment } from 'react'
import { DateHelper } from '$utils/date'
import { Role, Schedule } from '$api-client/types.gen'
import { useNavigate } from 'react-router'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { TeamRoutePath } from '../routes/protected'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'

export type TeamMemberWorkingHoursProps = {
  schedule: Schedule[]
}

export function TeamMemberWorkingHours({ schedule }: TeamMemberWorkingHoursProps) {
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const { hasSubscriptionExpired } = useTenant()

  const isEditDisabled = hasSubscriptionExpired || PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])

  const getTooltipContent = () => {
    if (hasSubscriptionExpired) {return SUBSCRIPTION_EXPIRED_MESSAGE}
    if (PermissionsHelper.doesntHaveRole(authUser.role, [Role.SUPER_ADMIN, Role.ADMIN])) {return 'You are not allowed to edit working hours.'}
    return undefined
  }

  return (
    <Card
      rounding="xl"
      padding="none"
    >
      <Card.Header
        padding="sm"
      >
        <div className="flex items-center justify-between gap-2 min-h-[30px]">
          <Text type="h4">
            Working Hours
          </Text>

          <Tooltip
            content={getTooltipContent()}
            disabled={!isEditDisabled}
          >
            <Button
              variant="secondary"
              size="sm"
              icon={EditIcon}
              onClick={() => navigate(TeamRoutePath.TeamMemberEditWorkingHours)}
              disabled={isEditDisabled}
            >
              Edit
            </Button>
          </Tooltip>
        </div>
      </Card.Header>

      <Card.Content
        padding="sm"
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-4">
          { schedule.sort((a, b) => Number(a.day) - Number(b.day)).map((schedule) => (
            <Fragment key={schedule.day}>
              <Text
                type="s"
                className="font-medium col-start-1"
              >
                {DAY_OF_WEEK[Number(schedule.day) as keyof typeof DAY_OF_WEEK]}
              </Text>

              <Text
                type="s"
                className="col-start-2"
              >
                { schedule.times.length !== 0
                  ? schedule.times.map((time) => DateHelper.formatTime(time.from) + ' - ' + DateHelper.formatTime(time.to)).join(', ')
                  : 'Day off'
                }
              </Text>
            </Fragment>
          )) }
        </div>
      </Card.Content>
    </Card>
  )
}
